import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import loginAPI, { LoginResponse, LoginSuccess, User } from './loginAPI';
import { getErrorMessage } from '../../utils/errorHandler';
import { RootState } from '../../store';
import { APIResponseError } from '../../services/api';

export interface LoginState {
  status: 'loading' | 'idle' | 'error';
  authed: boolean;
  error?: string;
  errorCode?: string;
  user?: User;
  logout?: string;
}

export const initialState: LoginState = {
  status: 'idle',
  authed: false,
};

// Ingresar utilizando un proveedor
export const authenticate = createAsyncThunk(
  'login/authenticate',
  async (provider: string) => {
    try {
      const response = await loginAPI.authenticate(provider);

      if (response.status !== 'ok') {
        throw new Error('Error de conexión al servidor de identidad');
      }

      return response;
    } catch (err) {
      throw new Error(getErrorMessage(err));
    }
  }
);

// Autenticarse en el backend
export const authorize = createAsyncThunk<
  LoginResponse,
  string,
  {
    rejectValue: APIResponseError,
  }
>(
  'login/authenticate',
  async (token: string, thunkAPI) => {
    try {
      const response = await loginAPI.authorize(token);

      // En caso de error devolvemos el payload completo para tener
      // más información del problema
      if (response.status !== 'ok') {
        return thunkAPI.rejectWithValue(response);
      }

      if (response.data.token) {
        localStorage.setItem('jwt_token', response.data.token);
      }

      return response.data;
    } catch (err) {
      throw new Error(getErrorMessage(err));
    }
  }
);

// Registrar datos usuario inexistente
export const register = createAsyncThunk<
  LoginSuccess,
  Record<string, string>,
  {
    rejectValue: APIResponseError,
  }
>(
  'login/register',
  async (data: Record<string, string>, thunkAPI) => {
    try {
      const response = await loginAPI.register(data);

      // En caso de error devolvemos el payload completo para tener
      // más información del problema
      if (response.status !== 'ok') {
        return thunkAPI.rejectWithValue(response);
      }

      localStorage.setItem('jwt_token', response.data.token);

      return response.data;
    } catch (err) {
      throw new Error(getErrorMessage(err));
    }
  }
);

// Edición de mail
export const changeEmail = createAsyncThunk(
  'login/changeEmail',
  async (data: Record<string, string>, thunkAPI) => {
    try {
      const response = await loginAPI.changeEmail(data);

      // En caso de error devolvemos el payload completo para tener
      // más información del problema
      if (response.status !== 'ok') {
        return thunkAPI.rejectWithValue(response);
      }

      return response.data;
    } catch (err) {
      throw new Error(getErrorMessage(err));
    }
  }
);

// Reenviar mail de verificación
export const resend = createAsyncThunk(
  'login/resend',
  async (token: string, thunkAPI) => {
    try {
      const response = await loginAPI.resend(token);

      if (response.status !== 'ok') {
        return thunkAPI.rejectWithValue(response);
      }

      return response.data;
    } catch (err) {
      throw new Error(getErrorMessage(err));
    }
  }
);

export const LoginState = createSlice({
  name: 'LoginState',
  initialState,
  reducers: {
    logout() {
      localStorage.removeItem('jwt_token');

      return {
        status: 'idle',
        authed: false,
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(authorize.pending, state => {
        state.status = 'loading';
        state.user = undefined;
      })
      .addCase(authorize.fulfilled, (_, action) => {
        // Sólo actualizamos el estado si volvió la información del usuario
        if (action.payload.message != 'USER_AUTHORIZED') {
          return initialState;
        }

        return {
          status: 'idle',
          user: action.payload.user,
          logout: action.payload.logout,
          authed: true,
        };
      })
      .addCase(authorize.rejected, (state, action) => {
        state.status = 'error';
        if (action.payload?.code) {
          state.errorCode = action.payload.code;
        }
        state.error = action.error.message;
      });

    builder
      .addCase(register.pending, state => {
        state.status = 'loading';
        state.user = undefined;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.status = 'idle';
        state.user = action.payload.user;
        state.logout = action.payload.logout;
        state.authed = true;
      })
      .addCase(register.rejected, (state, action) => {
        state.status = 'error';
        if (action.payload?.code) {
          state.errorCode = action.payload.code;
        }
        state.error = action.error.message;
      });
  },
});

export const getUser = (state: RootState) => state.login.user;
export const authed = (state: RootState) => state.login.authed ?? false;
export const getLogoutUrl = (state: RootState) => state.login.logout;
export const getLoginError = (state: RootState) => state.login.errorCode;
export const { logout } = LoginState.actions;

export default LoginState.reducer;
